import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Building2,
  Shield,
  Users,
  Bell,
  Key,
  Plus,
  Trash2,
  Loader2,
  Copy,
  Check,
} from 'lucide-react';
import { api, endpoints } from '@/lib/api-client';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { ThemeToggle } from '@/components/theme-toggle';

export const Route = createFileRoute('/_dashboard/settings')({
  component: SettingsPage,
});

interface SSOConnection {
  clientID: string;
  clientSecret: string;
  name: string;
  description?: string;
  tenant: string;
  product: string;
  defaultRedirectUrl: string;
  redirectUrl: string[];
  idpMetadata?: {
    provider?: string;
  };
  oidcProvider?: {
    discoveryUrl?: string;
  };
}

const PRODUCT = 'matricks';

function SettingsPage() {
  const { user } = useAuth();
  const [ssoConnections, setSsoConnections] = useState<SSOConnection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const tenant = user?.organizationId || 'default';

  useEffect(() => {
    fetchConnections();
  }, [tenant]);

  const fetchConnections = async () => {
    try {
      setIsLoading(true);
      const response = await api.get<{ connections: SSOConnection[] }>(
        endpoints.ssoConnections,
        { params: { tenant, product: PRODUCT } }
      );
      setSsoConnections(response.connections || []);
    } catch (error) {
      console.error('Failed to fetch SSO connections:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      toast.error('Failed to copy to clipboard');
    }
  };

  const getConnectionType = (conn: SSOConnection): 'saml' | 'oidc' => {
    return conn.oidcProvider?.discoveryUrl ? 'oidc' : 'saml';
  };

  const getProviderName = (conn: SSOConnection): string => {
    return conn.idpMetadata?.provider || conn.oidcProvider?.discoveryUrl || 'Unknown';
  };

  const acsUrl = `${import.meta.env.VITE_API_URL || 'http://localhost:4000'}/api/v1/sso/saml/acs`;
  const entityId = 'https://saml.matricks.ai';

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Settings</h2>
        <p className="text-muted-foreground">Manage your organization settings</p>
      </div>

      <Tabs defaultValue="organization" className="space-y-6">
        <TabsList>
          <TabsTrigger value="organization">Organization</TabsTrigger>
          <TabsTrigger value="appearance">Appearance</TabsTrigger>
          <TabsTrigger value="sso">SSO</TabsTrigger>
          <TabsTrigger value="team">Team</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
        </TabsList>

        {/* Organization Settings */}
        <TabsContent value="organization">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Organization Details
              </CardTitle>
              <CardDescription>
                Update your organization information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="org-name">Organization Name</Label>
                  <Input id="org-name" defaultValue="Matricks" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="org-slug">URL Slug</Label>
                  <Input id="org-slug" defaultValue="matricks" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="org-domain">Primary Domain</Label>
                <Input id="org-domain" defaultValue="matricks.ai" />
              </div>
              <Button>Save Changes</Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Appearance Settings */}
        <TabsContent value="appearance">
          <Card>
            <CardHeader>
              <CardTitle>Appearance</CardTitle>
              <CardDescription>
                Customize the appearance of the application
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Theme</p>
                  <p className="text-sm text-muted-foreground">
                    Select your preferred theme
                  </p>
                </div>
                <ThemeToggle />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SSO Settings */}
        <TabsContent value="sso">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="h-5 w-5" />
                      Single Sign-On (SSO)
                    </CardTitle>
                    <CardDescription>
                      Configure enterprise SSO for your organization
                    </CardDescription>
                  </div>
                  <Button>
                    <Plus className="mr-2 h-4 w-4" />
                    Add SSO Connection
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : ssoConnections.length > 0 ? (
                  <div className="space-y-4">
                    {ssoConnections.map((conn) => (
                      <div
                        key={conn.clientID}
                        className="flex items-center justify-between p-4 rounded-lg border"
                      >
                        <div className="flex items-center gap-4">
                          <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
                            <Key className="h-5 w-5" />
                          </div>
                          <div>
                            <p className="font-medium">{conn.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {getProviderName(conn)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <Badge variant="outline" className="uppercase">
                            {getConnectionType(conn)}
                          </Badge>
                          <Badge className="bg-green-500">active</Badge>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Shield className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium mb-2">No SSO connections</h3>
                    <p className="text-muted-foreground mb-4">
                      Set up SSO to allow team members to sign in with their corporate credentials
                    </p>
                    <Button>
                      <Plus className="mr-2 h-4 w-4" />
                      Add SSO Connection
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>SAML Configuration</CardTitle>
                <CardDescription>
                  Use these values to configure your identity provider
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>ACS URL (Assertion Consumer Service)</Label>
                  <div className="flex gap-2">
                    <Input readOnly value={acsUrl} />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => copyToClipboard(acsUrl, 'acs')}
                    >
                      {copiedField === 'acs' ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Entity ID / Audience</Label>
                  <div className="flex gap-2">
                    <Input readOnly value={entityId} />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => copyToClipboard(entityId, 'entity')}
                    >
                      {copiedField === 'entity' ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Team Settings */}
        <TabsContent value="team">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Team Members
                  </CardTitle>
                  <CardDescription>
                    Manage who has access to your organization
                  </CardDescription>
                </div>
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  Invite Member
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[
                  { name: 'Admin User', email: 'admin@acme.com', role: 'Admin' },
                  { name: 'Jane Smith', email: 'jane@acme.com', role: 'Member' },
                  { name: 'Bob Johnson', email: 'bob@acme.com', role: 'Member' },
                ].map((member, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between p-4 rounded-lg border"
                  >
                    <div className="flex items-center gap-4">
                      <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center">
                        <span className="text-sm font-medium text-primary-foreground">
                          {member.name.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium">{member.name}</p>
                        <p className="text-sm text-muted-foreground">{member.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Badge variant={member.role === 'Admin' ? 'default' : 'secondary'}>
                        {member.role}
                      </Badge>
                      <Button variant="outline" size="sm">
                        Edit
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications */}
        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Notification Preferences
              </CardTitle>
              <CardDescription>
                Configure how you receive notifications
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {[
                { title: 'Workflow Failures', description: 'Get notified when a workflow execution fails' },
                { title: 'Sync Errors', description: 'Get notified when an integration sync fails' },
                { title: 'Weekly Reports', description: 'Receive weekly usage reports' },
                { title: 'Security Alerts', description: 'Important security notifications' },
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{item.title}</p>
                    <p className="text-sm text-muted-foreground">{item.description}</p>
                  </div>
                  <input
                    type="checkbox"
                    defaultChecked={i < 2}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                </div>
              ))}
              <Separator />
              <Button>Save Preferences</Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
